import { Entity, IEntityEvents, IEntitySettings, IEntityPrivate } from "../../core/util/Entity";
import { Component } from "../../core/render/Component";
import { Color } from "../../core/util/Color";
import { Gradient } from "../../core/render/gradients/Gradient";
import { Percent } from "../../core/util/Percent";
import { Template } from "../../core/util/Template";
import { ListData } from "../../core/util/Data";

import * as $type from "../../core/util/Type";
import * as $array from "../../core/util/Array";
import * as $object from "../../core/util/Object";


export interface ISerializerSettings extends IEntitySettings {

	/**
	 * An array of settings to not include in the serialized data.
	 */
	excludeSettings?: Array<string>;

	/**
	 * An array of settings to include in the serialized data.
	 */
	includeSettings?: Array<string>;

	/**
	 * Include full values of these settings.
	 *
	 * @since 6.4.3
	 */
	fullSettings?: Array<string>;

	/**
	 * An array of properties to not include in the serialized data.
	 *
	 * @since 5.3.2
	 */
	excludeProperties?: Array<string>;

	/**
	 * An array of properties to include in the serialized data.
	 *
	 * @ignore
	 * @since 5.15.0
	 */
	includeProperties?: Array<string>;

	/**
	 * Maximum depth of recursion when traversing target object.
	 *
	 * @default 2
	 */
	maxDepth?: number;

	/**
	 * Include states in the output.
	 *
	 * @default false
	 * @since 5.15.0
	 */
	includeStates?: boolean;

	/**
	 * Include adapters in the output.
	 *
	 * @default false
	 * @since 5.15.0
	 */
	includeAdapters?: boolean;

	/**
	 * Include events in the output.
	 *
	 * @since 5.15.0
	 * @default false
	 * @todo implement
	 * @ignore
	 */
	includeEvents?: boolean;

	/**
	 * Serialize functions as strings or functions.
	 *
	 * @default "function"
	 */
	functionsAs?: "string" | "function";

}

export interface ISerializerPrivate extends IEntityPrivate {
}

export interface ISerializerEvents extends IEntityEvents {
}


/**
 * Provides functionality to serialize charts or individual elements into simple
 * objects or JSON.
 *
 * @see {@link https://www.amcharts.com/docs/v5/concepts/serializing/} for more info
 * @since 5.3.0
 */
export class Serializer extends Entity {

	public static className: string = "Serializer";
	public static classNames: Array<string> = Entity.classNames.concat([Serializer.className]);

	declare public _settings: ISerializerSettings;
	declare public _privateSettings: ISerializerPrivate;
	declare public _events: ISerializerEvents;

	protected _refs: { [index: string]: any } = {};

	/**
	 * Set while serializing data. A `#` or `@` at the start of a string means a
	 * reference in a config, so strings are escaped - but most of a data row is
	 * handed back untouched when parsed, and escaping it there would corrupt it
	 * a little more on every round trip. `_escapeParsed` puts the escaping back
	 * in the few places the parser does read values.
	 *
	 * @ignore
	 */
	protected _serializingData: boolean = false;

	/**
	 * Serializes data, which is values rather than configuration.
	 *
	 * @ignore
	 */
	public serializeData(values: Array<any>): unknown {
		const previous = this._serializingData;
		this._serializingData = true;
		const result = this.serialize(values, 0, true, true);
		this._serializingData = previous;
		this._escapeParsed(result);
		return result;
	}

	/**
	 * Escapes the strings the parser is going to read as references, and only
	 * those. It walks data the way the parser does: through every array, and
	 * into an object only when that object is marked `__parse`. Anywhere else a
	 * string comes back as it was written, so escaping it would leave a stray
	 * `##` in the data.
	 */
	private _escapeParsed(value: any): void {
		if ($type.isArray(value)) {
			$array.each(value, (item, index) => {
				if ($type.isString(item)) {
					value[index] = this._escapeText(item);
				}
				else {
					this._escapeParsed(item);
				}
			});
		}
		else if ($type.isObject(value) && (value as any).__parse === true) {
			$object.each(value as any, (key, item) => {
				if ($type.isString(item)) {
					(value as any)[key] = this._escapeText(item);
				}
				else {
					this._escapeParsed(item);
				}
			});
		}
	}

	/**
	 * Serializes target object into a simple object or JSON string.
	 *
	 * @param   source      Target object
	 * @param   depth       Current depth
	 * @param   full        Serialize object in full (ignoring maxDepth)
	 * @param   forceParse  If true, will add __parse to serialized objects
	 * @return              Serialized data
	 */
	public serialize(source: unknown, depth: number = 0, full: boolean = false, forceParse: boolean = false): unknown {
		if (depth > this.get("maxDepth", 2)) {
			return undefined;
		}

		if (source === false || source === true || source === null) {
			return source;
		}

		// A geometry built by one of the map's shape functions goes out as that
		// call - a handful of numbers rather than every coordinate it expands to
		const described: any = source && (source as any).__geometry;
		if (described) {
			const call: any = { type: described.name };
			$object.each(described.args, (key, value) => {
				call[key] = value;
			});
			return call;
		}

		if ($type.isArray(source)) {
			const res: any[] = [];
			$array.each(source, (arrval) => {
				res.push(this.serialize(arrval, depth, full, forceParse));
			});
			return res;
		}
		else if (source instanceof ListData) {
			const res: any[] = [];
			$array.each(source.values, (arrval) => {
				res.push(this.serialize(arrval, depth, full, forceParse));
			});
			return res;
		}
		const res: any = {};

		const am5object = source instanceof Entity || source instanceof Template || source instanceof Color || source instanceof Percent ? true : false;

		// Process settings
		const fullSettings: any = this.get("fullSettings", []);
		if (source instanceof Entity) {
			res.type = source.className;
			let settings: Array<string> = this._filterSettings($object.keys(source._settings));

			// Include only what was asked for - not what the chart worked out for
			// itself
			settings = settings.filter((value) => {
				return source.isUserSetting(value as any) && !source.isComputedSetting(value as any);
			});

			if (settings.length) {
				res.settings = {};
				$array.each(settings, (setting) => {
					const settingValue = (<any>source).get(setting);
					if (settingValue !== undefined) {
						res.settings[setting] = this.serialize(settingValue, depth + 1, full || fullSettings.indexOf(setting) !== -1);
						if (source instanceof Gradient && setting === "stops") {
							res.settings[setting] = this.serialize(settingValue, 0, true);
						}
					}
				});
			}
			this._processComputedEntities(source, res, settings, depth);
			this._processAdapters(source, res);
			this._processStates(source, res);
		}
		else if (source instanceof Template) {
			res.type = "Template";
			let settings: Array<string> = this._filterSettings($object.keys(source._settings));

			settings = settings.filter((value) => {
				return !source._computedSettings[value as never];
			});

			if (settings.length) {
				res.settings = {};
				$array.each(settings, (setting) => {
					if ((<any>source)._settings[setting] !== undefined) {
						res.settings[setting] = this.serialize((<any>source).get(setting), depth + 1, fullSettings.indexOf(setting) !== -1);
					}
				});
			}
			this._processAdapters(source, res);
			this._processStates(source, res);
			this._afterEntity(source, res);

			return res;
		}

		// Data
		if (source instanceof Component) {
			if (source.data.length && (this.get("excludeProperties")! || []).indexOf("data") === -1) {
				res.properties = {
					data: this.serializeData(source.data.values)
				};
			}
		}

		// Process the rest
		if (source instanceof Color) {
			return {
				type: "Color",
				value: source.toCSSHex()
			};
		}
		else if (source instanceof Percent) {
			return {
				type: "Percent",
				value: source.percent
			};
		}
		else if ($type.isString(source)) {
			return this._escapeRefs(source);
		}
		else if ($type.isNumber(source)) {
			return source;
		}
		else if ($type.isObject(source)) {
			// TODO
			if (full && !am5object) {
				const excludeProperties: Array<string> = this.get("excludeProperties", []);
				$object.each(source, (key, value) => {
					if (excludeProperties.indexOf(key) === -1 && value !== undefined) {
						res[key] = this.serialize(value, depth + 1, full, forceParse);
					}
				});
				if (forceParse) {
					// Add only if there are objects
					$object.each(res, (_key, value) => {
						if ($type.isObject(value)) {
							res.__parse = true;
						}
					});

					// A data row with its own `type` field reads as an entity to the
					// parser, which then goes looking for a class by that name. Saying
					// so outright keeps a column called `type` a column.
					if (res.type !== undefined && res.__parse !== true) {
						res.__parse = false;
					}
				}
			}
			else if (am5object) {
				const includeProperties: Array<string> = this.get("includeProperties", []);
				$array.each(includeProperties, (key) => {

					const value: any = (source as any)[key];
					if (value !== undefined) {
						if (res.properties === undefined) {
							res.properties = {};
						}
						res.properties[key] = this.serialize(value, depth + 1, full, forceParse);
					}
				});
			}
		}

		if (depth == 0 && $object.keys(this._refs).length) {
			res.refs = this._refs;
		}

		this._afterEntity(source, res);

		return res;
	}

	/**
	 * A place for a subclass to add to what an entity serialized to, wherever it
	 * was reached from.
	 *
	 * @ignore
	 */
	protected _afterEntity(_source: any, _res: any): void { }

	/**
	 * Writes out what was styled on an object the library built for itself.
	 *
	 * A background an element seeds, a part it configures - the object itself is
	 * not configuration, so it is not written out on its own account. What
	 * someone then styled on it is, and would otherwise be lost. It goes out
	 * without a `type`, so parsing configures the object already there instead of
	 * replacing it with a new one.
	 *
	 * @param   source    Source object
	 * @param   res       Result
	 * @param   written   Setting keys already written out in full
	 * @param   depth     Current depth
	 */
	private _processComputedEntities(source: Entity, res: any, written: Array<string>, depth: number): void {
		$array.each(this._filterSettings($object.keys(source._settings)), (key: any) => {
			if (written.indexOf(key) !== -1) {
				return;
			}

			const value: any = (<any>source._settings)[key];

			if (!(value instanceof Entity)) {
				return;
			}

			// An element the chart holds in its own right - an axis, a series,
			// another chart - is written out where it belongs and referenced from
			// here. Only the parts an element owns are configured in place.
			const part: any = value;

			if (part.isType && (part.isType("Axis") || part.isType("Series") || part.isType("Chart"))) {
				return;
			}

			// past `maxDepth` there is nothing to write
			const serialized: any = this.serialize(part, depth + 1, false);

			if (!serialized || typeof serialized !== "object") {
				return;
			}

			delete serialized.type;

			if (serialized.settings || serialized.properties || serialized.states || serialized.adapters) {
				if (!res.properties) {
					res.properties = {};
				}
				if (res.properties[key] === undefined) {
					res.properties[key] = serialized;
				}
			}
		});
	}

	private _processAdapters(source: any, res: any) {
		if (this.get("includeAdapters", false)) {
			const asString = this.get("functionsAs", "string") == "string";
			const callbacks = source.adapters._callbacks;
			if (Object.keys(callbacks).length > 0) {
				res.adapters = [];
				$object.each(callbacks, (key, callbackList) => {
					callbackList.forEach((callback: any) => {
						res.adapters.push({
							key: key,
							callback: asString ? callback.toString() : callback
						});
					});
				});
			}
		}
	}

	private _processStates(source: any, res: any) {
		if (this.get("includeStates", false)) {
			const states = source.states._states;
			if (Object.keys(states).length > 0) {
				const serialized: Array<any> = [];

				$object.each(states, (key: any, state: any) => {
					// Same rule as for settings - leave out what the chart worked out
					// for itself. A template's states have no computed settings of
					// their own: a template is configuration, so its states are too.
					const settings: any = {};
					const computed: any = state._computedSettings || {};

					$object.each(state._settings, (settingKey: any, value: any) => {
						if (!computed[settingKey]) {
							settings[settingKey] = value;
						}
					});

					if (Object.keys(settings).length > 0) {
						const serializedSettings: any = this.serialize(settings, 0, true);
						if (serializedSettings && Object.keys(serializedSettings).length > 0) {
							serialized.push({
								key: key,
								settings: serializedSettings
							});
						}
					}
				});

				if (serialized.length > 0) {
					res.states = serialized;
				}
			}
		}
	}

	private _filterSettings(settings: any) {
		const includeSettings: Array<string> = this.get("includeSettings", []);
		const excludeSettings: Array<string> = this.get("excludeSettings", []);
		if (includeSettings.length) {
			settings = includeSettings;
		}
		else if (excludeSettings.length) {
			settings = settings.filter((value: any) => {
				return excludeSettings.indexOf(value) === -1;
			});
		}
		return settings;
	}

	private _escapeRefs(text: string): string {
		if (this._serializingData) {
			return text;
		}
		return this._escapeText(text);
	}

	private _escapeText(text: string): string {
		return text.replace(/^#/, '##').replace(/^@/, '@@');
	}

}
